/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 *
 */

/**
 * @module bulb-list/bulblistui
 */

import {
  Plugin, createDropdown, addToolbarToDropdown, ButtonView,
} from 'ckeditor5';
import bulletedListIcon from '../../assets/block_toolbar_icons/bullet-list.svg';
import { BULB_CKE_LIST } from '../../editorcustomconfig.constant';

/**
 * The bulb list dropdown plugin.
 *
 * @extends Plugin
 */
export default class BulbListUi extends Plugin {

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbListUi' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    const { t } = editor;
    const { options } = BULB_CKE_LIST;

    // Register UI component.
    editor.ui.componentFactory.add('list', (locale) => {
      const dropdownView = createDropdown(locale);

      // Add existing list buttons to dropdown's toolbar.
      const buttons = options.map((option) => editor.ui.componentFactory.create(option));
      addToolbarToDropdown(dropdownView, buttons, {
        enableActiveItemFocusOnDropdownOpen: true,
        isVertical: false,
      });

      // Configure dropdown properties and behavior.
      dropdownView.buttonView.set({
        label: t('Lists'),
        tooltip: true,
      });

      dropdownView.extendTemplate({
        attributes: {
          class: 'ck-list-dropdown',
        },
      });

      // Change icon to reflect current selection's list.
      dropdownView.buttonView.bind('icon').toMany(buttons as ButtonView[], 'isOn', (...areActive) => {
        // Get the index of an active button.
        const index = areActive.findIndex((value) => value);

        // If none of the commands is active, display either defaultIcon or the first button's icon.
        if (index < 0) {
          return bulletedListIcon;
        }

        // Return active button's icon. TODO CG: update icon for button
        return bulletedListIcon; // buttons[index].icon;
      });

      // Enable button if any of the buttons is enabled.
      dropdownView.bind('isEnabled').toMany(
        buttons as ButtonView[],
        'isEnabled',
        (...areEnabled) => areEnabled.some((isEnabled) => isEnabled),
      );

      return dropdownView;
    });
  }
}
